import React from 'react';
import ReactDOM from 'react-dom';
import {
	Button,
	CssBaseline,
	ThemeProvider,
	createTheme,
	Dialog,
	Box,
	DialogActions,
	DialogContent,
	IconButton,
	Typography,
	DialogTitle,
	Alert,
	AlertTitle,
	CircularProgress,
} from '@mui/material';
import { Close, CheckCircle, CloseRounded, SimCardDownload } from '@mui/icons-material';
import Papa from 'papaparse';
import { useOnboardTheme } from '@onboardrs/onboard-component-library/theme';
import axios from 'axios';

export default function ImportLegalEntities({ companyId }) {
	const isLightTheme = true;
	const onboardTheme = useOnboardTheme(isLightTheme);
	const customTheme = createTheme({
		typography: {
			fontFamily: '"Inter", sans-serif',
			button: {
				fontWeight: 600,
			},
		},
	});
	const theme = {
		...onboardTheme,
		typography: customTheme.typography,
	};

	const [open, setOpen] = React.useState(false);
	const [dragOver, setDragOver] = React.useState(false);
	const [isValidFile, setIsValidFile] = React.useState(false);
	const [fileValidationMessage, setFileValidationMessage] = React.useState('');
	const [fileName, setFileName] = React.useState('');
	const [fileToUpload, setFileToUpload] = React.useState(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isImporting, setIsImporting] = React.useState(false);
	const [successMessage, setSuccessMessage] = React.useState('');
	const [responseData, setResponseData] = React.useState(null);
	const [fileId, setFileId] = React.useState('');
	const dataRef = React.useRef([]);
	const fileHeadersRef = React.useRef([]);
	const missingHeadersRef = React.useRef([]);
	const requiredHeadings = ['PMSUnitId', 'LegalEntity'];

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = (event, reason) => {
		if (reason === 'closebuttonclick') {
			setOpen(false);
			setIsImporting(false);
			setResponseData(null);
			setSuccessMessage('');
			setIsValidFile(false);
			setFileValidationMessage('');
			setFileName('');
			setFileToUpload(null);
			setFileId('');
		}
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setDragOver(false);
		setIsLoading(true);
		setIsValidFile(false);
		const file = event.dataTransfer.files[0];
		validateAndParseFile(file);
	};

	const handleDragOver = (event) => {
		event.preventDefault();
		setDragOver(true);
	};

	const handleDragLeave = () => {
		setDragOver(false);
	};

	const handleFileSelect = (event) => {
		setIsLoading(true);
		setIsValidFile(false);
		const file = event.target.files[0];
		validateAndParseFile(file);
		event.target.value = null;
	};

	const validateAndParseFile = (fileInput) => {
		dataRef.current = [];
		fileHeadersRef.current = [];
		missingHeadersRef.current = [];

		const step = (results) => {
			const row = results.data;
			if (fileHeadersRef.current.length == 0 || dataRef.current.length == 0) {
				const headers = Object.keys(row);
				fileHeadersRef.current = headers.filter((header) => requiredHeadings.includes(header));
				missingHeadersRef.current = requiredHeadings.filter((header) => !headers.includes(header));
			}

			const { PMSUnitId, LegalEntity } = row;
			if (PMSUnitId && LegalEntity) {
				const newRow = {
					PMSUnitId: row.PMSUnitId.toString().trim(),
					LegalEntity: row.LegalEntity.toString().trim(),
				};

				dataRef.current.push(newRow);
			}
		};

		const complete = () => {
			const isValid = missingHeadersRef.current.length == 0;
			setIsValidFile(isValid);
			setFileValidationMessage(isValid ? '' : 'Missing required headers: ' + missingHeadersRef.current.join(', '));
			setFileName(fileInput.name);

			if (isValid) {
				const csv = Papa.unparse(dataRef.current);
				const newFile = new File([csv], fileInput.name, { type: 'text/csv' });
				setFileToUpload(newFile);
			}

			setIsLoading(false);
		};
		Papa.parse(fileInput, { header: true, skipEmptyLines: true, step, complete });
	};

	const removeFile = () => {
		setIsValidFile(false);
		setFileToUpload(null);
		setFileValidationMessage('');
		setFileName('');
	};

	const downloadExampleCSV = () => {
		const csvData = [
			['LegalEntity', 'PMSUnitId'], // CSV headers
			['', ''],
		];
		const csvContent = Papa.unparse(csvData);
		const blob = new Blob([csvContent], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'example.csv');
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const downloadResults = () => {
		if (fileId) {
			window.open(`/files/${fileId}/download`);
		} else {
			window.history.pushState(null, null, '#tab-id=4');
		}
	};

	React.useEffect(() => {
		fetchJobData();
	}, []);

	const fetchJobData = async () => {
		await axios
			.get(
				`/api/jobMonitors?sort_by=created_at&sort_dir=desc&limit=1&jobable_type=App\\Company&jobable_id=${companyId}`
			)
			.then(async (response) => {
				const jobMonitor = response.data.JobMonitors[0];
				if (jobMonitor && jobMonitor.end_at == null && jobMonitor.error_at == null) {
					if (!isImporting) {
						setIsImporting(true);
					}
					setResponseData(jobMonitor);
				}
				if (jobMonitor && jobMonitor.end_at != null && jobMonitor.error_at == null && jobMonitor.progress == 100) {
					if (isImporting) {
						if (fileId) {
							await axios
								.get(`/files/${fileId}/contents`)
								.then((res) => {
									const csvData = Papa.parse(res.data, { header: true, skipEmptyLines: true });
									const data = csvData.data;
									const successCount = data.filter(
										(item) => item.Result === 'Success' || item.Result === 'Already updated'
									).length;
									const totalCount = data.length;
									setSuccessMessage(`${successCount}/${totalCount} units were successfully matched and updated.`);
									setIsImporting(false);
								})
								.catch((error) => {
									console.log(error);
								});
						} else {
							setSuccessMessage('Please download the results file.');
							setIsImporting(false);
						}
					}
				}
			})
			.catch((error) => {
				console.log(error);
			});
	};

	if (isImporting && responseData) {
		setTimeout(fetchJobData, 5000);
	}

	const importCSV = () => {
		setIsImporting(true);
		const formData = new FormData();
		formData.append('file', fileToUpload);
		formData.append('companyId', companyId);
		formData.append('totalRows', dataRef.current.length);
		axios
			.post('/units/importUnitLegalEntities', formData)
			.then((response) => {
				const data = { ...response.data, progress: 0 };
				setResponseData(data);
				setFileId(data.fileId);
			})
			.catch((error) => {
				setIsImporting(false);
				setIsValidFile(false);
				setFileValidationMessage(error.message || 'An error occurred while importing the file.');
			});
	};

	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem' }}>
				<Button variant="contained" color="secondary" onClick={handleClickOpen}>
					Import Legal Entities
				</Button>
			</div>
			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				{!isImporting && !responseData && (
					<div>
						<Box position="absolute" top={17} right={5}>
							<IconButton color="inherit" onClick={(e) => handleClose(e, 'closebuttonclick')} aria-label="close">
								<Close />
							</IconButton>
						</Box>
						<DialogTitle mt={0} mb={4}>
							<Typography variant="h6" component={'div'} fontWeight={600} letterSpacing="initial">
								Import legal entities
							</Typography>
						</DialogTitle>
						<DialogContent>
							<Box p="sm" mb={1}>
								<Alert severity="info" mb={3}>
									<AlertTitle>
										<Typography fontWeight={800}>Preparing your CSV</Typography>
									</AlertTitle>
									This feature should only be used for SFR clients when they are not providing legal entities via manual
									rent roll. Export the example CSV for the required format.
									<br />
									<Button
										variant="text"
										color="info"
										pr={-1}
										pt={5}
										onClick={downloadExampleCSV}
										endIcon={<SimCardDownload />}
									>
										Download CSV template
									</Button>
								</Alert>
							</Box>
							{!isValidFile && fileValidationMessage != '' && (
								<Box p="sm" m="sm">
									<Alert severity="error">
										<AlertTitle variant="h6">This file is invalid.</AlertTitle>
										{fileValidationMessage}
									</Alert>
								</Box>
							)}
							<Box
								onDrop={handleDrop}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								sx={{
									border: dragOver ? '2px dashed #40474E' : '1px solid #D3D3D3',
									backgroundColor: dragOver ? '#D3D3D3' : 'transparent',
									borderRadius: '8px',
									padding: '20px',
									textAlign: !fileToUpload && 'center',
									alignContent: !fileToUpload && 'center',
									cursor: 'pointer',
									marginTop: '20px',
									height: '165px',
								}}
							>
								<input type="file" style={{ display: 'none' }} id="fileInput" onChange={handleFileSelect} />
								{isLoading && <CircularProgress color="primary" />}
								{!isLoading && !fileToUpload && (
									<label htmlFor="fileInput">
										<p>
											Drag and drop or
											<Button variant="text" color="primary" component="span" size="medium">
												choose file
											</Button>
										</p>
									</label>
								)}
								{!isLoading && fileToUpload && (
									<Button
										variant="contained"
										endIcon={<CloseRounded />}
										onClick={removeFile}
										style={{ borderRadius: '50px' }}
									>
										{fileName}
									</Button>
								)}
							</Box>
						</DialogContent>
						<DialogActions>
							<Button variant="outlined" onClick={(e) => handleClose(e, 'closebuttonclick')} color="secondary">
								Cancel
							</Button>
							<Button
								variant="contained"
								onClick={importCSV}
								color="primary"
								disabled={isLoading || !isValidFile || !fileToUpload}
							>
								Import
							</Button>
						</DialogActions>
					</div>
				)}
				{isImporting && (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						marginTop={8}
						marginBottom={10}
					>
						<DialogContent>
							<Box display="flex" justifyContent="center" mb={5}>
								<Typography fontWeight={600} gutterBottom>
									Importing file...
								</Typography>
							</Box>
							<Box display="flex" justifyContent="center">
								{responseData ? (
									<Box sx={{ position: 'relative', display: 'inline-flex' }}>
										<CircularProgress variant="determinate" value={responseData.progress} />
										<Box
											sx={{
												top: 0,
												left: 0,
												bottom: 0,
												right: 0,
												position: 'absolute',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<Typography variant="caption" component="div" fontWeight={600}>{`${Math.round(
												responseData.progress
											)}%`}</Typography>
										</Box>
									</Box>
								) : (
									<CircularProgress />
								)}
							</Box>
						</DialogContent>
					</Box>
				)}
				{!isImporting && successMessage && (
					<>
						<Box position="absolute" top={17} right={5}>
							<IconButton color="inherit" onClick={(e) => handleClose(e, 'closebuttonclick')} aria-label="close">
								<Close />
							</IconButton>
						</Box>
						<DialogContent>
							<Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" marginTop={5}>
								<Box display="flex" justifyContent="center" mb={2}>
									<CheckCircle color="success" style={{ fontSize: 50 }} />
								</Box>
								<Box display="flex" justifyContent="center" mb={2}>
									<Typography fontWeight={600} gutterBottom>
										Your legal entity import is complete!
									</Typography>
								</Box>
								<Box display="flex" justifyContent="center" mb={2}>
									<Typography fontWeight={500} gutterBottom color="secondary">
										{successMessage}
									</Typography>
								</Box>
								<Box display="flex" justifyContent="center" textAlign={'center'}>
									{fileId && (
										<Button variant="text" onClick={downloadResults}>
											Download Results
										</Button>
									)}
								</Box>
							</Box>
						</DialogContent>
						<DialogActions>
							<Button variant="contained" onClick={(e) => handleClose(e, 'closebuttonclick')} color="primary" fullWidth>
								Close
							</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</ThemeProvider>
	);
}

const importLegalEntitiesElement = document.getElementById('import-legal-entities');

if (importLegalEntitiesElement) {
	const companyId = importLegalEntitiesElement.getAttribute('data-company-id');
	ReactDOM.render(<ImportLegalEntities companyId={companyId} />, importLegalEntitiesElement);
}
